import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { invalidateRef } from "../paymentDesk/shared/refDataStore";
import { Plus, Pencil, Ban, RotateCcw } from "lucide-react";

const MANAGE_ROLES = ["Director", "Manager", "Back Office"];

// Vendor Master — Vendor Category tab. Name + 3-letter uppercase code
// (unique) + active flag. Seeded with MAT/LAB/EQP/SVC. Director/Manager/
// Back Office add/edit/deactivate; Accounts views read-only.
export default function VendorCategoryTab() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get("/vendor-master/categories", { params: { include_inactive: includeInactive } })
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [includeInactive]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return toast.error("Category Name is required");
    if (newCode.trim().length !== 3) return toast.error("Category Code must be exactly 3 letters");
    setBusy(true);
    try {
      await client.post("/vendor-master/categories", { name: newName.trim(), code: newCode.trim() });
      toast.success("Vendor Category added");
      setNewName("");
      setNewCode("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (id) => {
    if (!editName.trim()) return toast.error("Category Name cannot be empty");
    if (editCode.trim().length !== 3) return toast.error("Category Code must be exactly 3 letters");
    setBusy(true);
    try {
      await client.put(`/vendor-master/categories/${id}`, { name: editName.trim(), code: editCode.trim() });
      toast.success("Vendor Category updated");
      invalidateRef("vendors");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (it) => {
    setBusy(true);
    try {
      await client.post(`/vendor-master/categories/${it.id}/${it.active ? "deactivate" : "reactivate"}`);
      toast.success(it.active ? "Category deactivated" : "Category reactivated");
      invalidateRef("vendors");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="vendor-category-tab">
      {canManage && (
        <form onSubmit={addItem} className="flex gap-2 max-w-lg">
          <Input data-testid="vendor-category-new-name-input" placeholder="New Category Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Input
            data-testid="vendor-category-new-code-input"
            placeholder="Code"
            className="w-24 font-mono uppercase"
            maxLength={3}
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
          />
          <Button type="submit" disabled={busy} data-testid="vendor-category-add-button" className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </form>
      )}

      <button
        data-testid="vendor-category-include-inactive-toggle"
        onClick={() => setIncludeInactive((v) => !v)}
        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          includeInactive ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
        }`}
      >
        {includeInactive ? "Showing All" : "Showing Active Only"}
      </button>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="vendor-category-table">
          <TableHeader>
            <TableRow>
              <TableHead>Category Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Status</TableHead>
              {canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">Loading...</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">No vendor categories found.</TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.id} data-testid={`vendor-category-row-${it.id}`}>
                  <TableCell>
                    {editing === it.id ? (
                      <Input className="h-8" data-testid={`vendor-category-edit-name-input-${it.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                    ) : (
                      <span className="font-medium">{it.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editing === it.id ? (
                      <Input
                        className="h-8 w-20 font-mono uppercase"
                        maxLength={3}
                        data-testid={`vendor-category-edit-code-input-${it.id}`}
                        value={editCode}
                        onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                      />
                    ) : (
                      <span className="font-mono text-xs font-semibold">{it.code}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={it.active === false ? "bg-slate-400" : "bg-emerald-600"}>{it.active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right space-x-2">
                      {editing === it.id ? (
                        <>
                          <Button size="sm" disabled={busy} data-testid={`vendor-category-save-${it.id}`} onClick={() => rename(it.id)} className="bg-amber-600 hover:bg-amber-700">
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`vendor-category-edit-${it.id}`}
                            onClick={() => {
                              setEditing(it.id);
                              setEditName(it.name);
                              setEditCode(it.code);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant={it.active === false ? "outline" : "destructive"}
                            disabled={busy}
                            data-testid={`vendor-category-toggle-active-${it.id}`}
                            onClick={() => toggleActive(it)}
                          >
                            {it.active === false ? <RotateCcw className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
